if (!customElements.get("product-finder-quiz")) {
  customElements.define(
    "product-finder-quiz",
    class ProductFinderQuiz extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.config = this.parseJson("[data-product-finder-config]", {});
        this.products = this.parseJson("[data-product-finder-products]", []);
        this.answers = new Map();
        this.currentQuestion = 1;
        this.selectedPad = null;
        this.selectedPadVariant = null;
        this.recommendations = [];
        this.complementaryRequestId = 0;
        this.isCompleting = false;

        this.screens = Array.from(this.querySelectorAll("[data-screen]"));
        this.status = this.querySelector("[data-quiz-status]");
        this.progress = this.querySelector("[data-progress]");
        this.progressBar = this.querySelector("[data-progress-bar]");
        this.progressText = this.querySelector("[data-progress-text]");
        this.resultList = this.querySelector("[data-result-list]");
        this.resultCards = Array.from(this.querySelectorAll("[data-result-card]"));
        this.emptyResult = this.querySelector("[data-empty-result]");
        this.complementaryProducts = this.querySelector("[data-complementary-products]");
        this.complementaryList = this.querySelector("[data-complementary-list]");
        this.setupComplementaryTrack();

        this.addEventListener("click", this.onClick.bind(this));
        this.addEventListener("change", this.onVariantChange.bind(this));

        this.status.hidden = false;
        this.showQuestion(this.currentQuestion);
        this.emitAnalytics("quiz_started");
      }

      setupComplementaryTrack() {
        const track = this.complementaryList;
        if (!track) return;

        this.complementaryPointerId = undefined;
        this.complementaryStartX = 0;
        this.complementaryStartScrollLeft = 0;
        this.complementaryDragged = false;
        this.complementarySuppressClick = false;

        track.addEventListener("pointerdown", (event) => this.onComplementaryPointerDown(event));
        track.addEventListener("pointermove", (event) => this.onComplementaryPointerMove(event));
        track.addEventListener("pointerup", (event) => this.endComplementaryDrag(event));
        track.addEventListener("pointercancel", (event) => this.endComplementaryDrag(event));
        track.addEventListener("click", (event) => this.onComplementaryTrackClick(event), true);
      }

      onComplementaryPointerDown(event) {
        if (event.pointerType === "touch" || event.button !== 0) return;

        this.complementaryPointerId = event.pointerId;
        this.complementaryStartX = event.clientX;
        this.complementaryStartScrollLeft = this.complementaryList.scrollLeft;
        this.complementaryDragged = false;
        this.complementarySuppressClick = false;
      }

      onComplementaryPointerMove(event) {
        if (event.pointerId !== this.complementaryPointerId) return;
        const distance = event.clientX - this.complementaryStartX;

        if (!this.complementaryDragged && Math.abs(distance) > 6) {
          this.complementaryDragged = true;
          this.complementarySuppressClick = true;
          this.complementaryList.classList.add("is-dragging");
          this.complementaryList.setPointerCapture?.(this.complementaryPointerId);
        }

        if (this.complementaryDragged) {
          this.complementaryList.scrollLeft = this.complementaryStartScrollLeft - distance;
          event.preventDefault();
        }
      }

      endComplementaryDrag(event) {
        if (event.pointerId !== this.complementaryPointerId) return;

        this.complementaryList.classList.remove("is-dragging");
        if (this.complementaryList.hasPointerCapture?.(this.complementaryPointerId)) {
          this.complementaryList.releasePointerCapture(this.complementaryPointerId);
        }
        this.complementaryPointerId = undefined;
        this.complementaryDragged = false;
      }

      onComplementaryTrackClick(event) {
        if (!this.complementarySuppressClick) return;

        event.preventDefault();
        event.stopPropagation();
        this.complementarySuppressClick = false;
      }

      parseJson(selector, fallback) {
        try {
          return JSON.parse(this.querySelector(selector)?.textContent || "");
        } catch (error) {
          console.error(`Product finder could not parse ${selector}`, error);
          return fallback;
        }
      }

      onClick(event) {
        const answerButton = event.target.closest("[data-answer]");
        const backButton = event.target.closest("[data-back]");
        const resultBackButton = event.target.closest("[data-result-back]");
        const restartButton = event.target.closest("[data-restart]");
        const addButton = event.target.closest("[data-add-to-cart]");
        const upsellAddButton = event.target.closest("[data-upsell-add-to-cart]");
        const productLink = event.target.closest("[data-see-product]");
        const complementaryLink = event.target.closest("[data-complementary-product]");

        if (answerButton) this.selectAnswer(answerButton);
        if (backButton) this.goBack();
        if (resultBackButton) this.returnToLastQuestion();
        if (restartButton) this.restartQuiz();
        if (addButton) this.addRecommendationToCart(event, addButton);
        if (upsellAddButton) this.addUpsellToCart(event, upsellAddButton);
        if (productLink) {
          const recommendation = this.getRecommendationState(productLink);
          this.emitAnalytics("cta_clicked", {
            action: "see_product",
            productId: recommendation?.product.id,
            variantId: recommendation?.selectedVariant.id,
            position: recommendation?.position,
          });
        }
        if (complementaryLink) {
          this.emitAnalytics("complementary_product_clicked", {
            productId: complementaryLink.dataset.productId,
            variantId: complementaryLink.dataset.variantId || undefined,
            source: complementaryLink.dataset.complementarySource || "search_discovery",
            sourceProductId: this.selectedPad?.id,
          });
        }
      }

      selectAnswer(button) {
        const question = button.dataset.question;
        const answer = {
          question,
          id: button.dataset.answerId || "",
          target: button.dataset.target || "context",
          value: button.dataset.preferredValue || "",
          weight: Number(button.dataset.weight || 0),
        };

        this.querySelectorAll(`[data-answer][data-question="${question}"]`).forEach((item) => {
          item.setAttribute("aria-pressed", String(item === button));
        });
        this.answers.set(question, answer);
        this.emitAnalytics("question_answered", answer);

        if (this.currentQuestion >= 4) {
          this.completeQuiz();
          return;
        }

        this.currentQuestion += 1;
        this.showQuestion(this.currentQuestion);
      }

      goBack() {
        if (this.currentQuestion <= 1) return;

        this.currentQuestion -= 1;
        this.showQuestion(this.currentQuestion);
      }

      returnToLastQuestion() {
        this.currentQuestion = 4;
        this.selectedPad = null;
        this.selectedPadVariant = null;
        this.complementaryRequestId += 1;
        this.clearComplementaryProducts();
        this.status.hidden = false;
        this.clearRecommendations();
        this.emptyResult.hidden = true;
        this.showQuestion(this.currentQuestion);
        this.emitAnalytics("results_back_clicked");
      }

      restartQuiz() {
        this.answers.clear();
        this.querySelectorAll("[data-answer]").forEach((button) => {
          button.setAttribute("aria-pressed", "false");
        });
        this.currentQuestion = 1;
        this.selectedPad = null;
        this.selectedPadVariant = null;
        this.complementaryRequestId += 1;
        this.clearComplementaryProducts();
        this.clearRecommendations();
        this.status.hidden = false;
        this.emptyResult.hidden = true;
        this.showQuestion(this.currentQuestion);
        this.emitAnalytics("quiz_restarted");
      }

      showQuestion(number) {
        this.renderProgress(number);
        this.showScreen(`q${number}`);
      }

      showScreen(name) {
        this.screens.forEach((screen) => {
          screen.hidden = screen.dataset.screen !== name;
        });

        const activeScreen = this.screens.find((screen) => screen.dataset.screen === name);
        const focusTarget = activeScreen?.matches('[tabindex="-1"]')
          ? activeScreen
          : Array.from(activeScreen?.querySelectorAll("button, a, select, input") || []).find(
              (element) => !element.closest("[hidden]")
            );
        window.requestAnimationFrame(() => focusTarget?.focus({ preventScroll: true }));
      }

      renderProgress(number) {
        const percentage = Math.min(100, Math.max(25, number * 25));
        this.progress.setAttribute("aria-valuenow", String(number));
        this.progressBar.style.width = `${percentage}%`;
        this.progressText.textContent = (this.config.labels?.progress || "Question [current] of [total]")
          .replace("[current]", String(number))
          .replace("[total]", "4");
      }

      resultRevealDelay() {
        return window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ? 250 : 1500;
      }

      async completeQuiz() {
        if (this.isCompleting) return;
        this.isCompleting = true;

        try {
          const preferences = this.buildPreferences();
          const rankedPads = this.rankMousepads(preferences);
          const matches = this.chooseRecommendations(rankedPads, 2);

          this.status.hidden = true;
          this.showScreen("loading");
          this.emitAnalytics("quiz_completed", {
            answers: Array.from(this.answers.values()).map((answer) => answer.id),
            candidateCount: rankedPads.length,
          });

          await new Promise((resolve) => window.setTimeout(resolve, this.resultRevealDelay()));
          if (!this.isConnected) return;

          if (!matches.length) {
            this.clearRecommendations();
            this.emptyResult.hidden = false;
            this.showScreen("result");
            return;
          }

          this.emptyResult.hidden = true;
          this.renderRecommendations(matches);
          this.showScreen("result");
          this.loadComplementaryProducts();
          this.recommendations.forEach((recommendation) => {
            this.emitAnalytics("product_recommended", {
              productId: recommendation.product.id,
              variantId: recommendation.selectedVariant.id,
              score: recommendation.match.score,
              position: recommendation.position,
            });
          });
        } finally {
          this.isCompleting = false;
        }
      }

      buildPreferences() {
        const preferences = {
          weighted: [],
          requiredSizes: [],
          requiredStyle: "",
          requiredPerformance: "",
          fullDesk: false,
        };

        this.answers.forEach((answer) => {
          if (answer.target === "required_size") {
            const requiredSizes = this.splitValues(answer.value);
            preferences.requiredSizes =
              answer.id === "full_desk"
                ? Array.from(new Set([...requiredSizes, "XL", "900X400MM"]))
                : requiredSizes;
            preferences.fullDesk = answer.id === "full_desk";
          } else if (answer.target === "style") {
            preferences.requiredStyle = answer.value;
          } else if (answer.target === "performance") {
            preferences.requiredPerformance = answer.value;
          } else if (answer.target === "material") {
            preferences.weighted.push(answer);
          }
        });

        return preferences;
      }

      rankMousepads(preferences) {
        const sizeMatchedPads = this.products
          .filter((product) => product.available)
          .map((product) => {
            const matchingVariants = this.findMatchingVariants(product, preferences.requiredSizes);
            if (!matchingVariants.length) return null;
            return { product, matchingVariants };
          })
          .filter(Boolean);

        const performanceMatchedPads = preferences.requiredPerformance
          ? sizeMatchedPads.filter((match) =>
              this.valueMatches(match.product.performance, preferences.requiredPerformance)
            )
          : sizeMatchedPads;
        const performancePool = performanceMatchedPads.length ? performanceMatchedPads : sizeMatchedPads;
        const styleMatchedPads = preferences.requiredStyle
          ? performancePool.filter((match) => this.valueMatches(match.product.style, preferences.requiredStyle))
          : performancePool;
        const eligiblePads = styleMatchedPads.length ? styleMatchedPads : performancePool;
        const rankedPool = new Map();

        eligiblePads.forEach((match) => {
          rankedPool.set(String(match.product.id), {
            ...match,
            fullDeskSelection: preferences.fullDesk,
            fullDeskPreferred: true,
          });
        });

        if (preferences.fullDesk) {
          const fullDeskSizeMatchedPads = sizeMatchedPads
            .map((match) => {
              const fullDeskVariants = this.findMatchingVariants(match.product, this.fullDeskPrimarySizes());
              return fullDeskVariants.length ? { ...match, fullDeskVariants } : null;
            })
            .filter(Boolean);
          const fullDeskPerformanceMatches = preferences.requiredPerformance
            ? fullDeskSizeMatchedPads.filter((match) =>
                this.valueMatches(match.product.performance, preferences.requiredPerformance)
              )
            : fullDeskSizeMatchedPads;
          const fullDeskPerformancePool = fullDeskPerformanceMatches.length
            ? fullDeskPerformanceMatches
            : fullDeskSizeMatchedPads;
          const fullDeskStyleMatches = preferences.requiredStyle
            ? fullDeskPerformancePool.filter((match) =>
                this.valueMatches(match.product.style, preferences.requiredStyle)
              )
            : fullDeskPerformancePool;
          const fullDeskPrimaryPads = fullDeskStyleMatches.length
            ? fullDeskStyleMatches
            : fullDeskPerformancePool;

          fullDeskPrimaryPads.forEach((match) => {
            const productId = String(match.product.id);
            rankedPool.set(productId, {
              ...(rankedPool.get(productId) || match),
              fullDeskSelection: true,
              fullDeskPrimary: true,
              fullDeskVariants: match.fullDeskVariants,
            });
          });
        }

        return Array.from(rankedPool.values())
          .map((match) => {
            const score = preferences.weighted.reduce((total, preference) => {
              const productValue = match.product[preference.target];
              return total + (this.valueMatches(productValue, preference.value) ? preference.weight : 0);
            }, 0);
            return { ...match, score };
          })
          .sort((a, b) => {
            if (b.score !== a.score) return b.score - a.score;
            if (Number(b.product.priority || 0) !== Number(a.product.priority || 0)) {
              return Number(b.product.priority || 0) - Number(a.product.priority || 0);
            }
            return String(a.product.id).localeCompare(String(b.product.id));
          });
      }

      chooseRecommendations(rankedPads, limit = 2) {
        const normalizedLimit = Math.max(0, limit);
        if (!rankedPads.length || normalizedLimit === 0) return [];

        if (rankedPads.some((match) => match.fullDeskSelection)) {
          const fullDeskPrimaryPads = rankedPads.filter((match) => match.fullDeskPrimary);
          if (fullDeskPrimaryPads.length) {
            const primaryTopScore = Math.max(...fullDeskPrimaryPads.map((match) => match.score));
            const primaryMatch = this.selectPriorityMatches(
              fullDeskPrimaryPads.filter((match) => match.score === primaryTopScore),
              1
            )[0];
            const fullDeskVariantIds = new Set(
              primaryMatch.fullDeskVariants.map((variant) => String(variant.id))
            );
            const primaryRecommendation = {
              ...primaryMatch,
              matchingVariants: [
                ...primaryMatch.fullDeskVariants,
                ...primaryMatch.matchingVariants.filter(
                  (variant) => !fullDeskVariantIds.has(String(variant.id))
                ),
              ],
            };
            if (normalizedLimit === 1) return [primaryRecommendation];

            const remainingPads = rankedPads.filter(
              (match) => String(match.product.id) !== String(primaryMatch.product.id)
            );
            const preferredRemainingPads = remainingPads.filter((match) => match.fullDeskPreferred);
            const secondaryPool = preferredRemainingPads.length ? preferredRemainingPads : remainingPads;
            if (!secondaryPool.length) return [primaryRecommendation];

            const secondaryTopScore = Math.max(...secondaryPool.map((match) => match.score));
            const secondaryMatch = this.selectPriorityMatches(
              secondaryPool.filter((match) => match.score === secondaryTopScore),
              1
            )[0];
            return [primaryRecommendation, secondaryMatch];
          }
        }

        const topScore = rankedPads[0].score;
        const exactMatches = rankedPads.filter((match) => match.score === topScore);
        return this.selectPriorityMatches(exactMatches, normalizedLimit);
      }

      fullDeskPrimarySizes() {
        return ["XXL", "3XL", "1200X500MM", "1000X500MM"];
      }

      selectPriorityMatches(matches, limit) {
        const normalizedLimit = Math.max(0, limit);
        if (!matches.length || normalizedLimit === 0) return [];

        const matchesByPriority = new Map();
        matches.forEach((match) => {
          const configuredPriority = Number(match.product.priority || 0);
          const priority = Number.isFinite(configuredPriority) ? configuredPriority : 0;
          if (!matchesByPriority.has(priority)) matchesByPriority.set(priority, []);
          matchesByPriority.get(priority).push(match);
        });

        return Array.from(matchesByPriority.entries())
          .sort(([priorityA], [priorityB]) => priorityB - priorityA)
          .flatMap(([, priorityMatches]) => this.shuffleMatches(priorityMatches))
          .slice(0, normalizedLimit);
      }

      shuffleMatches(matches) {
        const shuffled = [...matches];
        for (let index = shuffled.length - 1; index > 0; index -= 1) {
          const randomIndex = Math.floor(Math.random() * (index + 1));
          [shuffled[index], shuffled[randomIndex]] = [shuffled[randomIndex], shuffled[index]];
        }
        return shuffled;
      }

      findMatchingVariants(product, requiredSizes) {
        const availableVariants = (product.variants || []).filter((variant) => variant.available);
        if (!requiredSizes.length) return availableVariants;

        const matchingVariants = availableVariants.filter((variant) => {
          const variantValues = [variant.title, ...(variant.options || [])].map((value) => this.normalize(value));
          return requiredSizes.some((size) => variantValues.includes(this.normalize(size)));
        });

        if (matchingVariants.length) return matchingVariants;

        const hasVariantSizeOption = (product.optionNames || []).some((optionName) =>
          this.isSizeOption(optionName)
        );
        if (hasVariantSizeOption) return [];

        const taxonomySizes = (product.taxonomySizes || []).map((size) => this.normalize(size));
        const matchesTaxonomySize = requiredSizes.some((size) => taxonomySizes.includes(this.normalize(size)));
        return matchesTaxonomySize ? availableVariants : [];
      }

      isSizeOption(optionName) {
        return /^(size|taille|größe|groesse|taglia|tamaño|tamano|maat|dimension|dimensions)$/i.test(
          String(optionName).trim()
        );
      }

      renderRecommendations(matches) {
        this.clearRecommendations();
        this.resultList.hidden = false;

        this.recommendations = matches.map((match, index) => {
          const card = this.resultCards[index];
          card.hidden = false;
          return this.renderPad(match, card, index + 1);
        });

        const primaryRecommendation = this.recommendations[0];
        this.selectedPad = primaryRecommendation.product;
        this.selectedPadVariant = primaryRecommendation.selectedVariant;
      }

      clearRecommendations() {
        this.recommendations = [];
        this.resultCards.forEach((card) => {
          card.hidden = true;
          card.removeAttribute("data-product-id");
        });
        if (this.resultList) this.resultList.hidden = true;
      }

      renderPad(match, card, position) {
        const variantControl = card.querySelector("[data-variant-control]");
        const resultTitle = card.querySelector("[data-result-title]");
        const resultProfile = card.querySelector("[data-result-profile]");
        resultTitle.textContent = match.product.title;
        const profileValues = [match.product.performance, match.product.material].filter(Boolean);
        resultProfile.replaceChildren(
          ...profileValues.map((value) => {
            const tag = document.createElement("span");
            tag.className = "product-finder__result-profile-tag";
            tag.textContent = value;
            return tag;
          })
        );
        resultProfile.hidden = profileValues.length === 0;

        const recommendation = {
          card,
          match,
          position,
          product: match.product,
          selectedVariant: match.matchingVariants[0],
        };
        card.dataset.productId = String(match.product.id);
        this.renderVariantOptions(recommendation, variantControl);
        this.updatePadVariant(recommendation);
        return recommendation;
      }

      renderVariantOptions(recommendation, variantControl = recommendation.card.querySelector("[data-variant-control]")) {
        variantControl.replaceChildren();

        const variants = recommendation.match.matchingVariants;
        const optionCount = Math.max(0, ...variants.map((variant) => variant.options?.length || 0));
        const optionNames = recommendation.product.optionNames || [];
        let renderedGroups = 0;

        for (let optionIndex = 0; optionIndex < optionCount; optionIndex += 1) {
          const values = Array.from(
            new Set(variants.map((variant) => String(variant.options?.[optionIndex] || "").trim()).filter(Boolean))
          );
          if (!values.length || (values.length === 1 && this.normalize(values[0]) === "defaulttitle")) continue;

          const optionName = String(optionNames[optionIndex] || `Option ${optionIndex + 1}`);
          const isColor = this.isColorOption(optionName);
          const fieldset = document.createElement("fieldset");
          fieldset.className = `product-finder__variant-group${isColor ? " product-finder__variant-group--color" : ""}`;

          const legend = document.createElement("legend");
          legend.className = "product-finder__variant-legend body-normal";
          legend.textContent = `${optionName}: `;
          const selectedValue = document.createElement("span");
          selectedValue.textContent = recommendation.selectedVariant.options?.[optionIndex] || "";
          legend.appendChild(selectedValue);

          const choices = document.createElement("div");
          choices.className = "product-finder__variant-choices";

          values.forEach((value, valueIndex) => {
            const id = `ProductFinderOption-${this.config.sectionId}-${recommendation.position}-${optionIndex}-${valueIndex}`;
            const input = document.createElement("input");
            input.className = "product-finder__variant-input";
            input.type = "radio";
            input.id = id;
            input.name = `ProductFinderOption-${this.config.sectionId}-${recommendation.position}-${optionIndex}`;
            input.value = value;
            input.dataset.variantOption = "";
            input.dataset.optionIndex = String(optionIndex);
            input.checked = String(recommendation.selectedVariant.options?.[optionIndex] || "") === value;

            const label = document.createElement("label");
            label.className = `product-finder__variant-choice${isColor ? " product-finder__variant-swatch" : ""}`;
            label.htmlFor = id;
            label.title = value;

            const text = document.createElement("span");
            text.textContent = value;
            if (!isColor) {
              const check = document.createElementNS("http://www.w3.org/2000/svg", "svg");
              check.classList.add("product-finder__variant-check");
              check.setAttribute("width", "20");
              check.setAttribute("height", "20");
              check.setAttribute("viewBox", "0 0 20 20");
              check.setAttribute("fill", "none");
              check.setAttribute("aria-hidden", "true");
              const checkPath = document.createElementNS("http://www.w3.org/2000/svg", "path");
              checkPath.setAttribute("d", "M3 10.2632L7.48276 15L16 6");
              checkPath.setAttribute("stroke", "currentColor");
              checkPath.setAttribute("stroke-width", "2");
              checkPath.setAttribute("stroke-linecap", "round");
              checkPath.setAttribute("stroke-linejoin", "round");
              check.appendChild(checkPath);
              label.appendChild(check);
            }
            if (isColor) {
              text.className = "visually-hidden";
              const colorDot = document.createElement("span");
              colorDot.className = "product-finder__variant-color-dot";
              colorDot.setAttribute("aria-hidden", "true");
              colorDot.style.setProperty(
                "--product-finder-swatch-color",
                this.optionSwatchColor(recommendation.product, optionIndex, value)
              );
              label.appendChild(colorDot);
            }

            label.appendChild(text);
            choices.append(input, label);
          });

          fieldset.append(legend, choices);
          variantControl.appendChild(fieldset);
          renderedGroups += 1;
        }

        variantControl.hidden = renderedGroups === 0;
      }

      isColorOption(optionName) {
        return /^(color|colour|couleur|farbe|colore|kleur|cor)$/i.test(String(optionName).trim());
      }

      optionSwatchColor(product, optionIndex, value) {
        const option = product.optionSwatches?.[optionIndex];
        const swatch = option?.values?.find((item) => String(item.value) === String(value));
        return swatch?.color || "#737373";
      }

      variantLabel(variant, product) {
        const title = String(variant.title || "").trim();
        return this.normalize(title) === "defaulttitle" ? product.title : title;
      }

      getRecommendationState(element) {
        const card = element?.closest?.("[data-result-card]");
        return this.recommendations.find((recommendation) => recommendation.card === card);
      }

      onVariantChange(event) {
        const optionInput = event.target.closest?.("[data-variant-option]");
        if (!optionInput) return;

        const recommendation = this.getRecommendationState(optionInput);
        if (!recommendation) return;

        const optionIndex = Number(optionInput.dataset.optionIndex);
        const requestedOptions = [...(recommendation.selectedVariant.options || [])];
        requestedOptions[optionIndex] = optionInput.value;
        const exactVariant = recommendation.match.matchingVariants.find(
          (variant) => (variant.options || []).every((value, index) => String(value) === String(requestedOptions[index]))
        );
        const compatibleVariant = recommendation.match.matchingVariants.find(
          (variant) => String(variant.options?.[optionIndex] || "") === optionInput.value
        );
        recommendation.selectedVariant = exactVariant || compatibleVariant || recommendation.selectedVariant;
        if (recommendation.position === 1) this.selectedPadVariant = recommendation.selectedVariant;
        this.renderVariantOptions(recommendation);
        this.updatePadVariant(recommendation);
      }

      updatePadVariant(recommendation) {
        if (!recommendation?.product || !recommendation.selectedVariant) return;

        const resultImage = recommendation.card.querySelector("[data-result-image]");
        const resultPrice = recommendation.card.querySelector("[data-result-price]");
        const seeProductLink = recommendation.card.querySelector("[data-see-product]");
        const image = recommendation.selectedVariant.image || recommendation.product.image;
        if (image) {
          resultImage.src = image;
        } else {
          resultImage.removeAttribute("src");
        }
        resultImage.alt = recommendation.product.imageAlt || recommendation.product.title;
        resultPrice.textContent = recommendation.selectedVariant.priceFormatted || this.formatMoney(recommendation.selectedVariant.price);
        seeProductLink.href = `${recommendation.product.url}?variant=${recommendation.selectedVariant.id}`;
      }

      clearComplementaryProducts() {
        this.complementaryList?.replaceChildren();
        if (this.complementaryProducts) {
          this.complementaryProducts.hidden = true;
          this.complementaryProducts.removeAttribute("aria-busy");
        }
      }

      upsellLimit() {
        return Math.min(4, Math.max(1, Number(this.config.complementaryLimit || 3)));
      }

      recommendationsUrl(productId, intent) {
        const root = window.Shopify?.routes?.root || "/";
        const normalizedRoot = root.endsWith("/") ? root : `${root}/`;
        const requestLimit = Math.min(10, this.upsellLimit() * 2);
        const normalizedIntent = intent === "related" ? "related" : "complementary";
        return `${normalizedRoot}recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=${requestLimit}&intent=${normalizedIntent}`;
      }

      async fetchRecommendationProducts(productId, intent) {
        const response = await fetch(this.recommendationsUrl(productId, intent), {
          headers: { Accept: "application/json" },
        });
        if (!response.ok) {
          throw new Error(`${intent} recommendations request failed with ${response.status}`);
        }
        const result = await response.json();
        return Array.isArray(result.products) ? result.products : [];
      }

      async loadComplementaryProducts() {
        this.clearComplementaryProducts();

        const sourceProducts = this.recommendations
          .map((recommendation) => recommendation.product)
          .filter((product) => product?.id);
        if (
          !this.config.complementaryEnabled ||
          !sourceProducts.length ||
          !this.complementaryProducts ||
          !this.complementaryList
        ) return;

        const requestId = ++this.complementaryRequestId;
        const limit = this.upsellLimit();
        const sourceProductIds = sourceProducts.map((product) => String(product.id));
        const excludedProductIds = new Set([
          ...(Array.isArray(this.config.cartProductIds) ? this.config.cartProductIds : []).map(String),
          ...sourceProductIds,
        ]);
        const seenVariantIds = new Set();
        const seenProductIds = new Set();
        const discountedProducts = [];

        for (const sourceProduct of sourceProducts) {
          const upsells = Array.isArray(sourceProduct.discountedUpsells)
            ? sourceProduct.discountedUpsells
            : [];

          for (const upsell of upsells) {
            const productId = String(upsell?.productId || "");
            const variantId = String(upsell?.variantId || "");
            if (
              discountedProducts.length >= limit ||
              !productId ||
              !variantId ||
              upsell.available === false ||
              excludedProductIds.has(productId) ||
              seenVariantIds.has(variantId)
            ) continue;

            discountedProducts.push({
              ...upsell,
              id: upsell.productId,
              isDiscountedUpsell: true,
            });
            seenVariantIds.add(variantId);
            seenProductIds.add(productId);
          }

          if (discountedProducts.length >= limit) break;
        }

        this.complementaryProducts.setAttribute("aria-busy", "true");

        try {
          const regularProducts = [];
          if (discountedProducts.length < limit) {
            const requests = sourceProducts.flatMap((product) => [
              this.fetchRecommendationProducts(product.id, "complementary"),
              this.fetchRecommendationProducts(product.id, "related"),
            ]);
            const settledResults = await Promise.allSettled(requests);
            if (requestId !== this.complementaryRequestId) return;

            const failedResults = settledResults.filter((result) => result.status === "rejected");
            const searchProducts = settledResults.flatMap((result) =>
              result.status === "fulfilled" ? result.value : []
            );
            if (failedResults.length === settledResults.length && !discountedProducts.length) {
              throw failedResults[0].reason;
            }

            for (const product of searchProducts) {
              const productId = String(product?.id || "");
              if (
                discountedProducts.length + regularProducts.length >= limit ||
                !productId ||
                product.available === false ||
                excludedProductIds.has(productId) ||
                seenProductIds.has(productId)
              ) continue;
              regularProducts.push({ ...product, isDiscountedUpsell: false });
              seenProductIds.add(productId);
            }
          }

          if (requestId !== this.complementaryRequestId) return;

          const products = [...discountedProducts, ...regularProducts];
          products.forEach((product) =>
            this.complementaryList.appendChild(this.createComplementaryCard(product))
          );
          this.complementaryProducts.hidden = products.length === 0;
          this.emitAnalytics("complementary_products_loaded", {
            sourceProductId: sourceProducts[0].id,
            sourceProductIds,
            productIds: products.map((product) => product.id),
            discountedVariantIds: discountedProducts.map((product) => product.variantId),
          });
        } catch (error) {
          if (requestId !== this.complementaryRequestId) return;
          this.complementaryProducts.hidden = true;
          console.warn("Product finder could not load upsell products", error);
        } finally {
          if (requestId === this.complementaryRequestId) {
            this.complementaryProducts.removeAttribute("aria-busy");
          }
        }
      }

      createComplementaryCard(product) {
        const card = document.createElement("article");
        card.className = "product-finder__complementary-card";
        if (product.isDiscountedUpsell) {
          card.classList.add("product-finder__complementary-card--discounted");
        }

        const applyLinkTracking = (link) => {
          link.dataset.complementaryProduct = "";
          link.dataset.productId = String(product.id || product.productId || "");
          link.dataset.complementarySource = product.isDiscountedUpsell
            ? "discounted_upsell"
            : "search_discovery";
          if (product.variantId) link.dataset.variantId = String(product.variantId);
        };

        const mediaLink = document.createElement("a");
        mediaLink.className = "product-finder__complementary-media-link";
        mediaLink.href = product.url || "#";
        mediaLink.setAttribute("aria-label", product.title || "");
        mediaLink.draggable = false;
        applyLinkTracking(mediaLink);

        const imageWrap = document.createElement("div");
        imageWrap.className = "product-finder__complementary-media";
        const imageSource =
          product.image ||
          product.featured_image?.url ||
          product.featured_image ||
          product.images?.[0];
        if (imageSource) {
          const image = document.createElement("img");
          image.className = "product-finder__complementary-image";
          image.src = imageSource;
          image.alt = product.imageAlt || product.featured_image?.alt || product.title || "";
          image.width = 480;
          image.height = 480;
          image.loading = "lazy";
          image.draggable = false;
          imageWrap.appendChild(image);
        }
        mediaLink.appendChild(imageWrap);

        const body = document.createElement("div");
        body.className = "product-finder__complementary-body";
        const top = document.createElement("div");
        top.className = "product-finder__complementary-top";

        const title = document.createElement("h4");
        title.className = "product-finder__complementary-title";
        const titleLink = document.createElement("a");
        titleLink.href = product.url || "#";
        titleLink.textContent = product.title || "";
        applyLinkTracking(titleLink);
        title.appendChild(titleLink);
        top.appendChild(title);

        if (
          product.isDiscountedUpsell &&
          product.variantTitle &&
          product.variantTitle !== "Default Title"
        ) {
          const variant = document.createElement("span");
          variant.className = "product-finder__complementary-variant body-xs";
          variant.textContent = product.variantTitle;
          top.appendChild(variant);
        }

        const price = document.createElement("p");
        price.className = "product-finder__complementary-price";
        if (
          product.isDiscountedUpsell &&
          product.discountedPrice !== null &&
          product.discountedPrice !== undefined
        ) {
          price.classList.add("product-finder__complementary-price--sale");
          const salePrice = document.createElement("span");
          salePrice.className = "product-finder__complementary-sale-price";
          salePrice.textContent = this.formatMoney(product.discountedPrice);
          const regularPrice = document.createElement("s");
          regularPrice.className = "product-finder__complementary-regular-price";
          regularPrice.textContent = this.formatMoney(product.price);
          price.append(salePrice, regularPrice);
        } else {
          price.textContent = this.formatMoney(product.price);
        }
        top.appendChild(price);
        body.appendChild(top);

        const actions = document.createElement("div");
        actions.className = "product-finder__complementary-actions";
        const variants = Array.isArray(product.variants) ? product.variants : [];
        const directVariant = product.isDiscountedUpsell
          ? { id: product.variantId, available: product.available }
          : variants.length === 1
            ? variants[0]
            : null;

        if (directVariant?.id && directVariant.available !== false) {
          const error = document.createElement("p");
          error.className = "product-finder__error body-xs";
          error.setAttribute("role", "alert");
          error.dataset.upsellCartError = "";
          error.hidden = true;

          const button = document.createElement("button");
          button.type = "button";
          button.className = "product-finder__complementary-button button button--primary";
          button.dataset.upsellAddToCart = "";
          button.dataset.variantId = String(directVariant.id);
          button.dataset.productId = String(product.id || product.productId || "");
          button.dataset.complementarySource = product.isDiscountedUpsell
            ? "discounted_upsell"
            : "search_discovery";
          button.setAttribute("aria-live", "polite");
          button.setAttribute("aria-label", this.config.labels?.addToCart || "Add to cart");

          const buttonContent = document.createElement("span");
          buttonContent.className = "button__content";
          const buttonLabel = document.createElement("span");
          buttonLabel.className = "button__label";
          buttonLabel.dataset.buttonLabel = "";
          buttonLabel.dataset.label = this.config.labels?.addToCart || "Add to cart";
          buttonLabel.textContent = this.config.labels?.addToCart || "Add to cart";
          buttonContent.appendChild(buttonLabel);

          const animatedCircle = document.createElement("span");
          animatedCircle.className = "animated-button-circle";
          button.append(buttonContent, animatedCircle);
          actions.append(error, button);
        } else {
          const productButton = document.createElement("a");
          productButton.className = "product-finder__complementary-button button button--transparent";
          productButton.href = product.url || "#";
          applyLinkTracking(productButton);

          const buttonContent = document.createElement("span");
          buttonContent.className = "button__content";
          const buttonLabel = document.createElement("span");
          buttonLabel.className = "button__label";
          buttonLabel.dataset.label = this.config.labels?.seeProduct || "View product";
          buttonLabel.textContent = this.config.labels?.seeProduct || "View product";
          buttonContent.appendChild(buttonLabel);
          const animatedCircle = document.createElement("span");
          animatedCircle.className = "animated-button-circle";
          productButton.append(buttonContent, animatedCircle);
          actions.appendChild(productButton);
        }

        body.appendChild(actions);
        card.append(mediaLink, body);
        return card;
      }

      async addUpsellToCart(event, addButton) {
        const variantId = Number(addButton.dataset.variantId);
        const productId = String(addButton.dataset.productId || "");
        if (!variantId || addButton.disabled) return;

        const card = addButton.closest(".product-finder__complementary-card");
        const cartError = card?.querySelector("[data-upsell-cart-error]");
        const items = [{ id: variantId, quantity: 1 }];

        addButton.disabled = true;
        if (cartError) cartError.hidden = true;
        this.setAddButtonLabel(addButton, this.config.labels?.adding || "Adding…");
        this.emitAnalytics("cta_clicked", {
          action: "add_upsell_to_cart",
          productId,
          variantId,
          source: addButton.dataset.complementarySource || "search_discovery",
        });

        const cart = document.querySelector("cart-drawer");
        const body = { items };
        if (cart && typeof cart.getSectionsToRender === "function") {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
        }

        try {
          const response = await fetch(window.routes?.cart_add_url || "/cart/add.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify(body),
          });
          const result = await response.json();
          if (!response.ok || result.status) throw new Error(result.description || result.message);

          if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined" && PUB_SUB_EVENTS.cartUpdate) {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: "product-finder-quiz",
              productVariantId: variantId,
            });
          }

          if (cart && result.sections && typeof cart.renderContents === "function") {
            cart.setActiveElement?.(addButton);
            cart.renderContents(result, event);
          } else {
            window.location.href = window.routes?.cart_url || "/cart";
            return;
          }

          this.setAddButtonLabel(addButton, this.config.labels?.added || "Added");
          this.config.cartProductIds = [
            ...new Set([...(Array.isArray(this.config.cartProductIds) ? this.config.cartProductIds : []), productId]),
          ];
          this.loadComplementaryProducts();
        } catch (error) {
          if (cartError) {
            cartError.textContent =
              error.message || this.config.labels?.cartError || "Could not add the recommendation to your cart.";
            cartError.hidden = false;
          }
          this.setAddButtonLabel(addButton, this.config.labels?.addToCart || "Add to cart");
        } finally {
          addButton.disabled = false;
        }
      }

      async addRecommendationToCart(event, addButton) {
        const recommendation = this.getRecommendationState(addButton);
        if (!recommendation?.selectedVariant || addButton.disabled) return;

        const cartError = recommendation.card.querySelector("[data-cart-error]");
        const items = [{ id: recommendation.selectedVariant.id, quantity: 1 }];

        addButton.disabled = true;
        cartError.hidden = true;
        this.setAddButtonLabel(addButton, this.config.labels?.adding || "Adding…");
        this.emitAnalytics("cta_clicked", {
          action: "add_to_cart",
          productId: recommendation.product.id,
          variantId: recommendation.selectedVariant.id,
          position: recommendation.position,
        });

        const cart = document.querySelector("cart-drawer");
        const body = { items };
        if (cart && typeof cart.getSectionsToRender === "function") {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
        }

        try {
          const response = await fetch(window.routes?.cart_add_url || "/cart/add.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify(body),
          });
          const result = await response.json();
          if (!response.ok || result.status) throw new Error(result.description || result.message);

          if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined" && PUB_SUB_EVENTS.cartUpdate) {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: "product-finder-quiz",
              productVariantId: recommendation.selectedVariant.id,
            });
          }

          if (cart && result.sections && typeof cart.renderContents === "function") {
            cart.setActiveElement?.(addButton);
            cart.renderContents(result, event);
          } else {
            window.location.href = window.routes?.cart_url || "/cart";
            return;
          }

          this.setAddButtonLabel(addButton, this.config.labels?.added || "Added");
        } catch (error) {
          cartError.textContent = error.message || this.config.labels?.cartError || "Could not add the recommendation to your cart.";
          cartError.hidden = false;
          this.setAddButtonLabel(addButton, this.config.labels?.addToCart || "Add to cart");
        } finally {
          addButton.disabled = false;
        }
      }

      setAddButtonLabel(button, label) {
        const labelElement = button.querySelector("[data-button-label]");
        if (!labelElement) return;
        labelElement.textContent = label;
        labelElement.dataset.label = label;
      }

      valueMatches(actual, preferred) {
        const actualValues = Array.isArray(actual) ? actual : [actual];
        const preferredValues = this.splitValues(preferred);
        return actualValues.some((actualValue) =>
          preferredValues.some((preferredValue) => this.normalize(actualValue) === this.normalize(preferredValue))
        );
      }

      splitValues(value) {
        return String(value || "")
          .split("|")
          .map((item) => item.trim())
          .filter(Boolean);
      }

      normalize(value) {
        return String(value || "")
          .toLocaleLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .replace(/[×✕]/g, "x")
          .replace(/[^a-z0-9]/g, "");
      }

      formatMoney(cents) {
        const locale = this.config.locale || document.documentElement.lang || "en";
        const currency = this.config.currency || window.Shopify?.currency?.active || "EUR";
        const value = Number(cents || 0) / 100;
        const template = String(this.config.moneyFormat || "");
        const token = template.match(/\{\{\s*(\w+)\s*\}\}/)?.[1];

        if (token) {
          const noDecimals = token.includes("no_decimals");
          const separatorLocale = token.includes("comma_separator")
            ? "de-DE"
            : token.includes("space_separator")
              ? "fr-FR"
              : token.includes("apostrophe_separator")
                ? "de-CH"
                : "en-US";
          const amount = new Intl.NumberFormat(separatorLocale, {
            minimumFractionDigits: noDecimals ? 0 : 2,
            maximumFractionDigits: noDecimals ? 0 : 2,
          }).format(value);
          return template.replace(/\{\{\s*\w+\s*\}\}/, amount).replace(/<[^>]*>/g, "");
        }

        try {
          return new Intl.NumberFormat(locale, { style: "currency", currency }).format(value);
        } catch (error) {
          return `${value.toFixed(2)} ${currency}`;
        }
      }

      emitAnalytics(name, payload = {}) {
        const detail = {
          event: name,
          sectionId: this.config.sectionId,
          ...payload,
        };
        this.dispatchEvent(
          new CustomEvent("phantum:product-finder", {
            bubbles: true,
            detail,
          })
        );
        if (Array.isArray(window.dataLayer)) {
          window.dataLayer.push({ event: `phantum_product_finder_${name}`, ...detail });
        }
      }
    }
  );
}
